import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { UserPlus, Pencil, UserX, Loader2, ShieldCheck, AlertTriangle, Users } from 'lucide-react'
import {
  listSystemUsers,
  createSystemUser,
  updateSystemUser,
  deactivateSystemUser,
  type SystemUser,
  type CreateUserPayload,
  type UpdateUserPayload,
} from '@/api/system-users'
import { getPlanUsage } from '@/api/config'
import { useAuth } from '@/hooks/useAuth'
import { ApiError } from '@/lib/api-error'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Superadmin',
  admin: 'Admin',
  recepcionista: 'Recepcionista',
}

const ROLE_STYLE: Record<string, string> = {
  superadmin: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  admin:      'bg-accent/10 text-accent border-accent/20',
  recepcionista: 'bg-background text-text-muted border-border',
}

interface PlanInfo { current: number; limit: number; enforcement: boolean }

// ── Form modal ───────────────────────────────────────────────────────────────

interface UserFormModalProps {
  open: boolean
  user?: SystemUser
  onClose: () => void
  onSaved: () => void
}

function UserFormModal({ open, user, onClose, onSaved }: UserFormModalProps) {
  const isEdit = Boolean(user)
  const [name, setName]         = useState(user?.name ?? '')
  const [email, setEmail]       = useState(user?.email ?? '')
  const [password, setPassword] = useState('')
  const [role, setRole]         = useState<'admin' | 'recepcionista'>(
    (user?.role === 'admin' || user?.role === 'recepcionista') ? user.role : 'recepcionista'
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setName(user?.name ?? '')
      setEmail(user?.email ?? '')
      setPassword('')
      setRole((user?.role === 'admin' || user?.role === 'recepcionista') ? user.role : 'recepcionista')
    }
  }, [open, user])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      if (isEdit && user) {
        const payload: UpdateUserPayload = {}
        if (name !== user.name) payload.name = name
        if (email !== user.email) payload.email = email
        if (password) payload.password = password
        if (role !== user.role) payload.role = role
        await updateSystemUser(user.id, payload)
        toast.success('Usuario actualizado')
      } else {
        const payload: CreateUserPayload = { name, email, password, role }
        await createSystemUser(payload)
        toast.success('Usuario creado')
      }
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50 transition-all'

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-surface border-border text-text-primary max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
              <UserPlus className="w-4 h-4 text-accent" />
            </div>
            <DialogTitle className="text-base font-bold">
              {isEdit ? 'Editar usuario' : 'Nuevo usuario del sistema'}
            </DialogTitle>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-3">
          {/* Nombre */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Nombre completo"
              className={inputCls}
            />
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              className={inputCls}
            />
          </div>

          {/* Contraseña */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
              Contraseña{isEdit && <span className="ml-1 text-text-muted font-normal normal-case">(dejar vacío para no cambiar)</span>}
              {!isEdit && <span className="text-red-500"> *</span>}
            </label>
            <PasswordInput
              value={password}
              onChange={e => setPassword(e.target.value)}
              required={!isEdit}
              minLength={6}
              placeholder={isEdit ? '••••••••' : 'Mínimo 6 caracteres'}
              className={inputCls}
            />
          </div>

          {/* Rol */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">Rol</label>
            <div className="grid grid-cols-2 gap-2">
              {(['recepcionista', 'admin'] as const).map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`py-2.5 px-4 rounded-xl border text-sm font-semibold transition-colors cursor-pointer ${
                    role === r
                      ? 'bg-accent/10 border-accent/30 text-accent'
                      : 'bg-background border-border text-text-muted hover:border-accent/20 hover:text-text-primary'
                  }`}
                >
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim() || !email.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-accent text-background text-sm font-bold rounded-xl hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear usuario'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export function SystemUsers() {
  const { user: currentUser } = useAuth()
  const [users, setUsers]         = useState<SystemUser[]>([])
  const [planInfo, setPlanInfo]   = useState<PlanInfo | null>(null)
  const [loading, setLoading]     = useState(true)
  const [modal, setModal]         = useState<'create' | SystemUser | null>(null)
  const [confirmTarget, setConfirmTarget] = useState<SystemUser | null>(null)
  const [deactivating, setDeactivating]   = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [{ users: u }, planRes] = await Promise.all([
        listSystemUsers(),
        getPlanUsage().catch(() => null),
      ])
      setUsers(u)
      if (planRes) {
        const resource = planRes.resources.find(r => r.key === 'system_users')
        if (resource) setPlanInfo({ current: resource.current, limit: resource.limit, enforcement: planRes.enforcement })
      }
    } catch {
      toast.error('Error al cargar usuarios')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleDeactivateConfirm() {
    if (!confirmTarget) return
    setDeactivating(true)
    try {
      await deactivateSystemUser(confirmTarget.id)
      toast.success('Usuario desactivado')
      setConfirmTarget(null)
      load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error al desactivar')
    } finally {
      setDeactivating(false)
    }
  }

  const isAtLimit = Boolean(planInfo && planInfo.enforcement && planInfo.current >= planInfo.limit)
  const pct       = planInfo && planInfo.limit > 0 ? Math.min(100, Math.round((planInfo.current / planInfo.limit) * 100)) : 0

  return (
    <div className="flex flex-col gap-6">

      {/* Form modal */}
      <UserFormModal
        open={modal !== null}
        user={modal !== 'create' ? (modal as SystemUser) : undefined}
        onClose={() => setModal(null)}
        onSaved={load}
      />

      {/* Deactivate confirm */}
      <Dialog open={!!confirmTarget} onOpenChange={o => !o && setConfirmTarget(null)}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader>
            <DialogTitle>Desactivar usuario</DialogTitle>
          </DialogHeader>
          <p className="text-text-muted text-sm mt-2">
            ¿Desactivar a <strong className="text-text-primary">{confirmTarget?.name}</strong>? No podrá iniciar sesión hasta que sea reactivado manualmente.
          </p>
          <DialogFooter className="mt-4">
            <button onClick={() => setConfirmTarget(null)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors cursor-pointer">
              Cancelar
            </button>
            <button
              onClick={handleDeactivateConfirm}
              disabled={deactivating}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {deactivating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Desactivar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-text-primary font-bold text-xl leading-tight">Usuarios del sistema</h1>
          <p className="text-text-muted text-xs mt-1">
            Cuentas con acceso al panel de administración
            {!loading && users.length > 0 && (
              <span className="ml-2 font-semibold text-text-primary">· {users.filter(u => u.active).length} activos</span>
            )}
          </p>
        </div>
        <button
          onClick={() => !isAtLimit && setModal('create')}
          disabled={isAtLimit}
          title={isAtLimit ? `Límite alcanzado (${planInfo?.current}/${planInfo?.limit})` : undefined}
          className={`flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl transition-colors shrink-0 ${
            isAtLimit
              ? 'bg-text-muted/20 text-text-muted cursor-not-allowed'
              : 'bg-accent text-background hover:bg-accent-hover cursor-pointer shadow-lg shadow-accent/20'
          }`}
        >
          {isAtLimit ? <AlertTriangle className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
          Nuevo usuario
        </button>
      </div>

      {/* Plan quota bar */}
      {planInfo && (
        <div className={`bg-surface border rounded-2xl px-5 py-4 flex items-center gap-4 ${isAtLimit ? 'border-red-500/30' : 'border-border'}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isAtLimit ? 'bg-red-500/10 border border-red-500/20' : 'bg-accent/10 border border-accent/15'}`}>
            <ShieldCheck className={`w-5 h-5 ${isAtLimit ? 'text-red-500' : 'text-accent'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-text-primary text-sm font-semibold">
                {planInfo.current} / {planInfo.limit} usuarios
              </span>
              {planInfo.enforcement ? (
                <span className={`text-xs font-bold tabular-nums ${isAtLimit ? 'text-red-500' : pct >= 80 ? 'text-amber-500' : 'text-text-muted'}`}>
                  {pct}%
                </span>
              ) : (
                <span className="text-xs text-text-muted">Sin límite activo</span>
              )}
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden border border-border">
              <div
                className={`h-full rounded-full transition-all duration-500 ${isAtLimit ? 'bg-red-500' : pct >= 80 ? 'bg-amber-400' : 'bg-accent'}`}
                style={{ width: planInfo.enforcement ? `${pct}%` : '25%', opacity: planInfo.enforcement ? 1 : 0.3 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </div>
      ) : users.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-10 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center">
            <Users className="w-7 h-7 text-accent" />
          </div>
          <div>
            <p className="text-text-primary font-semibold text-sm">Sin usuarios creados</p>
            <p className="text-text-muted text-xs mt-1">Crea el primer usuario del sistema para dar acceso al panel.</p>
          </div>
          <button
            onClick={() => setModal('create')}
            className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer"
          >
            <UserPlus className="w-4 h-4" /> Crear primer usuario
          </button>
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-background/40">
                <th className="text-left px-5 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest">Usuario</th>
                <th className="text-left px-5 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest hidden md:table-cell">Email</th>
                <th className="text-left px-5 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest">Rol</th>
                <th className="text-left px-5 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest">Estado</th>
                <th className="text-left px-5 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest hidden lg:table-cell">Alta</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const ini = u.name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
                const isSelf = u.id === currentUser?.sub
                const canEdit = u.role !== 'superadmin'

                return (
                  <tr
                    key={u.id}
                    className={`border-b border-border last:border-0 transition-colors hover:bg-background/30 ${!u.active ? 'opacity-50' : ''}`}
                  >
                    {/* Nombre + avatar */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-accent/15 border border-accent/20 flex items-center justify-center shrink-0">
                          <span className="text-accent text-[10px] font-bold">{ini}</span>
                        </div>
                        <div>
                          <p className="text-text-primary font-semibold text-sm leading-tight">
                            {u.name}
                            {isSelf && <span className="ml-1.5 text-[9px] font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded-full">Tú</span>}
                          </p>
                          <p className="text-text-muted text-[10px] md:hidden mt-0.5 font-mono">{u.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="px-5 py-3.5 text-text-muted text-xs font-mono hidden md:table-cell">{u.email}</td>

                    {/* Rol */}
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border ${ROLE_STYLE[u.role] ?? ROLE_STYLE.recepcionista}`}>
                        {ROLE_LABELS[u.role] ?? u.role}
                      </span>
                    </td>

                    {/* Estado */}
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${
                        u.active
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                          : 'bg-red-500/10 text-red-500 border-red-500/20'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${u.active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        {u.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>

                    {/* Alta */}
                    <td className="px-5 py-3.5 text-text-muted text-xs hidden lg:table-cell">
                      {new Date(u.created_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>

                    {/* Acciones */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <button
                            onClick={() => setModal(u)}
                            aria-label="Editar"
                            className="p-1.5 rounded-lg text-text-muted hover:text-accent hover:bg-accent/10 transition-colors cursor-pointer"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canEdit && u.active && !isSelf && (
                          <button
                            onClick={() => setConfirmTarget(u)}
                            aria-label="Desactivar"
                            className="p-1.5 rounded-lg text-text-muted hover:text-red-500 hover:bg-red-500/10 transition-colors cursor-pointer"
                          >
                            <UserX className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
